// src/modules/units/tyres/views/action/tyreassociationform.js
//
// Sensor-association orchestrator: Fit, Move and Remove.
//
// Fit / Move deliberately use a CONTROLLED available-sensor picker. The user may
// type to SEARCH the returned choices, but that text is never submitted as a
// sensor id: a real option has to be selected first. This prevents a typed/scanned
// arbitrary id from bypassing the availability rules.
//
// Available sensors come from GET /api/customer/{customerId}/sensor via
// available-sensors.api.js. That includes newly assembled sensors that may not yet
// exist in global.units. Application is record metadata only and never filters the
// sensor choices.
//
// A 409 is treated as a stale availability race, not as permission to evict the
// other tyre. The list is refreshed and the user must choose an available sensor.
//
// Remove still clears the current association. All writes announce themselves via
// tyres.api.js -> TYRE_EVENTS.CHANGED.

import { associateTyreWithSensor, clearTyreSensorAssociation, updateTyreRecordInfo } from '../tyres.api.js';
import { fetchAvailableSensors } from '../available-sensors.api.js';
import { associationFormHTML } from './tyreassociationform.view.js';

const isBlank = (v) => v === null || v === undefined || v === '';

export function renderTyreAssociationForm(viewEl, ctx = {}) {
  let disposed = false;
  let busy = false;
  let sensorLoading = false;
  let selectedSensorId = null;
  let sensorChoices = [];
  let visibleChoices = [];
  let activeOptionIndex = -1;
  const sensorAbort = new AbortController();

  const tyre = ctx.tyre || {};
  const mode = ctx.mode || 'fit';
  const id = tyre.externalTyreId;

  const tpl = document.createElement('template');
  tpl.innerHTML = associationFormHTML(mode, tyre);
  const root = tpl.content.firstElementChild;
  const msgEl = root.querySelector('[data-msg]');
  const submitBtn = root.querySelector('[data-submit]');
  const cancelBtn = root.querySelector('[data-cancel]');
  const sensorInput = root.querySelector('[data-field="sensorSearch"]');
  const sensorOptions = root.querySelector('[data-sensor-options]');
  const sensorHint = root.querySelector('[data-sensor-hint]');
  const appInput = root.querySelector('[data-field="application"]');
  const submitLabel = submitBtn ? submitBtn.textContent : '';

  viewEl.replaceChildren(root);

  function setMsg(text, kind) {
    msgEl.textContent = text || '';
    msgEl.hidden = !text;
    msgEl.className = 'tm-form-msg' + (kind ? ` tm-form-msg-${kind}` : '');
  }

  function updateSubmitState() {
    if (!submitBtn) return;
    if (mode === 'remove') return;
    submitBtn.disabled = busy || sensorLoading || !selectedSensorId;
  }

  function setBusy(on, label) {
    busy = on;
    if (submitBtn) submitBtn.textContent = on ? (label || 'Working…') : submitLabel;
    if (cancelBtn) cancelBtn.disabled = on;
    if (sensorInput) sensorInput.disabled = on || sensorLoading;
    updateSubmitState();
  }

  function done() { close(); }
  function close() { if (typeof ctx.onClose === 'function') ctx.onClose(); }

  function setPickerOpen(open) {
    if (!sensorOptions || !sensorInput) return;
    const shouldOpen = Boolean(open && visibleChoices.length);
    sensorOptions.hidden = !shouldOpen;
    sensorInput.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
  }

  function setSensorHint(text, kind = '') {
    if (!sensorHint) return;
    sensorHint.textContent = text || '';
    sensorHint.hidden = !text;
    sensorHint.className = `tm-sensor-hint${kind ? ` tm-sensor-hint-${kind}` : ''}`;
  }

  function clearSelection({ keepText = true } = {}) {
    selectedSensorId = null;
    activeOptionIndex = -1;
    if (!keepText && sensorInput) sensorInput.value = '';
    if (sensorInput) sensorInput.removeAttribute('data-selected-sensor-id');
    updateSubmitState();
  }

  function selectSensor(choice) {
    if (!choice || !sensorInput) return;
    selectedSensorId = choice.id;
    sensorInput.value = choice.id;
    sensorInput.dataset.selectedSensorId = choice.id;
    activeOptionIndex = -1;
    setPickerOpen(false);
    setSensorHint('Selected from available sensors.', 'ok');
    updateSubmitState();
  }

  function renderChoices(filterText = '') {
    if (!sensorOptions) return;
    const needle = String(filterText || '').trim().toLowerCase();

    visibleChoices = sensorChoices
      .filter((choice) => !needle || choice.id.toLowerCase().includes(needle))
      .slice(0, 80);

    activeOptionIndex = -1;
    sensorOptions.replaceChildren();

    visibleChoices.forEach((choice, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'tm-sensor-option';
      button.dataset.sensorOption = choice.id;
      button.setAttribute('role', 'option');
      button.setAttribute('aria-selected', 'false');
      button.textContent = choice.id;
      button.addEventListener('mousedown', (event) => {
        // mousedown selects before the input blur closes the list.
        event.preventDefault();
        selectSensor(choice);
      });
      sensorOptions.appendChild(button);
    });

    if (!visibleChoices.length) {
      setPickerOpen(false);
      setSensorHint(
        needle ? 'No available sensors match your search.' : 'No available sensors found.',
        'warn'
      );
      return;
    }

    setSensorHint(`${sensorChoices.length} available sensor${sensorChoices.length === 1 ? '' : 's'}. Select one from the list.`);
  }

  function paintActiveOption() {
    if (!sensorOptions) return;
    const buttons = [...sensorOptions.querySelectorAll('[data-sensor-option]')];
    buttons.forEach((button, index) => {
      const active = index === activeOptionIndex;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-selected', active ? 'true' : 'false');
      if (active) button.scrollIntoView({ block: 'nearest' });
    });
  }

  async function loadSensors({ preserveSearch = false } = {}) {
    if (!sensorInput || disposed) return;

    sensorLoading = true;
    selectedSensorId = null;
    sensorInput.disabled = true;
    if (!preserveSearch) sensorInput.value = '';
    setPickerOpen(false);
    setSensorHint('Loading available sensors…');
    updateSubmitState();

    try {
      const res = await fetchAvailableSensors({ signal: sensorAbort.signal });
      if (disposed) return;

      if (!res.success) {
        sensorChoices = [];
        visibleChoices = [];
        setSensorHint(res.message || 'Could not load available sensors.', 'error');
        setMsg('Available sensors could not be loaded. Please try again.', 'error');
        return;
      }

      sensorChoices = Array.isArray(res.data?.results) ? res.data.results : [];
      renderChoices(sensorInput.value);
    } catch (error) {
      if (disposed || error?.name === 'AbortError') return;
      sensorChoices = [];
      visibleChoices = [];
      setSensorHint('Could not load available sensors.', 'error');
      setMsg('Available sensors could not be loaded. Please try again.', 'error');
    } finally {
      if (!disposed) {
        sensorLoading = false;
        sensorInput.disabled = busy;
        updateSubmitState();
      }
    }
  }

  // Optionally sync the application onto the record before associating.
  async function maybeUpdateApplication() {
    if (!appInput) return { success: true };
    const app = appInput.value;
    if (isBlank(app) || app === (tyre.tyreApplication || '')) return { success: true };
    // Just the one field: PUT /info leaves omitted fields untouched, and echoing
    // the rest would resend a stale tyreKm / tyreTreadDepth over the server's.
    return updateTyreRecordInfo(id, { tyreApplication: app }, { reason: 'info' });
  }

  async function associate(sensorId) {
    return associateTyreWithSensor(id, { sensorId, ifExistingTyre: 'error' }, { reason: mode });
  }

  async function submitFitMove() {
    if (busy || !id) return;
    const sensorId = selectedSensorId;
    if (!sensorId) {
      setMsg('Please select a sensor from the available sensor list.', 'error');
      return;
    }

    // Do not trust text remaining in the search box as the selected id.
    if (sensorInput?.dataset.selectedSensorId !== sensorId || sensorInput?.value !== sensorId) {
      clearSelection();
      setMsg('Please select a sensor from the available sensor list.', 'error');
      return;
    }

    setMsg('', '');
    setBusy(true, mode === 'move' ? 'Moving…' : 'Fitting…');

    try {
      const appRes = await maybeUpdateApplication();
      if (disposed) return;
      if (!appRes.success) {
        setMsg(appRes.message || 'Could not update the tyre record.', 'error');
        setBusy(false);
        return;
      }

      const res = await associate(sensorId);
      if (disposed) return;

      if (res.success) { done(); return; }

      if (res.status === 409) {
        // The sensor became occupied after the list was loaded. Never offer a
        // destructive "remove the other tyre" path from an available-only picker.
        setBusy(false);
        clearSelection({ keepText: false });
        setMsg(`Sensor ${sensorId} is no longer available. Please select another sensor.`, 'error');
        await loadSensors();
        return;
      }

      setMsg(res.message || 'Could not complete the association.', 'error');
      setBusy(false);
    } catch (e) {
      if (!disposed) {
        setMsg('Something went wrong. Please try again.', 'error');
        setBusy(false);
      }
    }
  }

  async function submitRemove() {
    if (busy || !id) return;
    setMsg('', '');
    setBusy(true, 'Removing…');
    try {
      const res = await clearTyreSensorAssociation(id, { reason: 'remove' });
      if (disposed) return;
      if (res.success) { done(); return; }
      setMsg(res.message || 'Could not remove the tyre from its sensor.', 'error');
      setBusy(false);
    } catch (e) {
      if (!disposed) {
        setMsg('Something went wrong. Please try again.', 'error');
        setBusy(false);
      }
    }
  }

  if (sensorInput) {
    sensorInput.addEventListener('input', () => {
      clearSelection();
      renderChoices(sensorInput.value);
      setPickerOpen(true);
    });

    sensorInput.addEventListener('focus', () => {
      renderChoices(sensorInput.value);
      setPickerOpen(true);
    });

    sensorInput.addEventListener('keydown', (event) => {
      if (!visibleChoices.length) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        activeOptionIndex = Math.min(activeOptionIndex + 1, visibleChoices.length - 1);
        paintActiveOption();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        activeOptionIndex = Math.max(activeOptionIndex - 1, 0);
        paintActiveOption();
      } else if (event.key === 'Enter' && activeOptionIndex >= 0) {
        event.preventDefault();
        selectSensor(visibleChoices[activeOptionIndex]);
      } else if (event.key === 'Escape') {
        setPickerOpen(false);
      }
    });

    sensorInput.addEventListener('blur', () => {
      // Give an option mousedown a chance to run first.
      window.setTimeout(() => setPickerOpen(false), 0);
    });
  }

  if (submitBtn) submitBtn.addEventListener('click', mode === 'remove' ? submitRemove : submitFitMove);
  if (cancelBtn) cancelBtn.addEventListener('click', () => { if (!busy) close(); });

  if (mode !== 'remove') {
    updateSubmitState();
    loadSensors();
  }

  return () => {
    disposed = true;
    sensorAbort.abort();
  };
}
